// src/hooks/useRentmanData.js
import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const CACHE_KEY = 'rentman_data_cache';
const CACHE_TIMESTAMP_KEY = 'rentman_data_cache_timestamp';
const CACHE_VERSION_KEY = 'rentman_data_cache_version';

export function useRentmanData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheVersion, setCacheVersion] = useState(null);
  const [cacheTTL, setCacheTTL] = useState(1209600); // Default to 14 days in seconds

  // Function to clear cache
  const clearCache = () => {
    // console.log('Clearing cache...');
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    localStorage.removeItem(CACHE_VERSION_KEY);
    // console.log('Cache cleared. Current localStorage state:', {
    //   data: localStorage.getItem(CACHE_KEY),
    //   timestamp: localStorage.getItem(CACHE_TIMESTAMP_KEY),
    //   version: localStorage.getItem(CACHE_VERSION_KEY)
    // });
  };

  // Function to update cache with new data
  const updateCache = (newData, version) => {
    // console.log('Updating cache with version:', version);
    const currentTime = Date.now();
    localStorage.setItem(CACHE_KEY, JSON.stringify(newData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, currentTime.toString());
    localStorage.setItem(CACHE_VERSION_KEY, version.toString());
    // console.log('Cache updated. Current localStorage state:', {
    //   data: localStorage.getItem(CACHE_KEY) ? 'present' : 'missing',
    //   timestamp: localStorage.getItem(CACHE_TIMESTAMP_KEY),
    //   version: localStorage.getItem(CACHE_VERSION_KEY)
    // });
  };

  // Function to check and sync cache version
  const checkAndSyncCacheVersion = (firestoreVersion) => {
    const cachedVersion = parseInt(localStorage.getItem(CACHE_VERSION_KEY) || '0');
    // console.log('Checking cache version:', { firestoreVersion, cachedVersion });
    
    if (cachedVersion !== firestoreVersion) {
      // console.log('Version mismatch detected - clearing cache');
      // clearCache();
      // Update the version immediately after clearing
      localStorage.setItem(CACHE_VERSION_KEY, firestoreVersion.toString());
      return true; // Indicates cache was cleared
    }
    return false; // Indicates no action was needed
  };

  // Separate function for setting up Firestore listeners
  const setupFirestoreListeners = useCallback(() => {
    const cacheVersionRef = doc(db, 'system', 'cacheVersion');
    const cacheTTLRef = doc(db, 'system', 'cacheTTL');
    
    // Set up real-time listener for cache version changes
    const unsubscribeVersion = onSnapshot(cacheVersionRef, (doc) => {
      if (doc.exists) {
        const docData = doc.data();
        if (docData && typeof docData.version !== 'undefined') {
          const version = docData.version;
          // console.log('Cache version updated from Firestore:', version);
          
          // Check and sync cache version
          const wasCleared = checkAndSyncCacheVersion(version);
          setCacheVersion(version);
          
          // If cache was cleared, trigger a refresh
          if (wasCleared) {
            refreshData();
          }
        } else {
          // console.log('Document exists but missing version field, using default version 1');
          setCacheVersion(1);
          clearCache(); // Clear cache if version field is missing
        }
      } else {
        // console.log('No cache version document found, using default version 1');
        setCacheVersion(1);
        clearCache(); // Clear cache if no version document exists
      }
    }, (error) => {
      console.error('Error listening to cache version:', error);
      setCacheVersion(1); // Fallback to version 1 on error
      clearCache(); // Clear cache on error
    });

    // Set up real-time listener for cache TTL changes
    const unsubscribeTTL = onSnapshot(cacheTTLRef, (doc) => {
      if (doc.exists()) {
        const ttl = doc.data().ttl;
        // console.log('Cache TTL updated from Firestore:', ttl);
        setCacheTTL(ttl);
      } else {
        // console.log('No cache TTL document found, using default TTL');
        setCacheTTL(1209600); // Default to 14 days in seconds
      }
    }, (error) => {
      console.error('Error listening to cache TTL:', error);
      setCacheTTL(1209600); // Fallback to default TTL on error
    });

    // Initial version check
    const checkInitialVersion = async () => {
      try {
        const versionDoc = await getDoc(cacheVersionRef);
        if (versionDoc.exists()) {
          const docData = versionDoc.data();
          if (docData && typeof docData.version !== 'undefined') {
            const version = docData.version;
            // console.log('Initial version check:', { firestoreVersion: version });
            checkAndSyncCacheVersion(version);
          } else {
            // console.log('Document exists but missing version field - clearing cache');
            clearCache();
          }
        } else {
          // console.log('No initial version document found - clearing cache');
          clearCache();
        }
      } catch (error) {
        console.error('Error checking initial version:', error);
        clearCache();
      }
    };

    checkInitialVersion();

    return () => {
      unsubscribeVersion();
      unsubscribeTTL();
    };
  }, []);

  // Set up Firestore listeners for cache version and TTL
  useEffect(() => {
    // In development, add a small delay to prevent Fast Refresh issues
    if (import.meta.env.DEV) {
      const timer = setTimeout(() => {
        const cleanup = setupFirestoreListeners();
        return cleanup;
      }, 100);
      return () => clearTimeout(timer);
    } else {
      return setupFirestoreListeners();
    }
  }, [setupFirestoreListeners]);

  // Fetch data when cache version or TTL changes
  useEffect(() => {
    if (cacheVersion === null) return; // Wait for initial cache version

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Check cache first
        const cachedData = localStorage.getItem(CACHE_KEY);
        const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        const cachedVersion = parseInt(localStorage.getItem(CACHE_VERSION_KEY) || '0');
        const currentTime = Date.now();

        // console.log('Cache check:', {
        //   hasCachedData: !!cachedData,
        //   cachedTimestamp,
        //   cachedVersion,
        //   firestoreVersion: cacheVersion,
        //   versionsMatch: cachedVersion === cacheVersion,
        //   cacheAge: cachedTimestamp ? currentTime - parseInt(cachedTimestamp) : 'N/A',
        //   cacheTTL: cacheTTL * 1000 // Convert to milliseconds for comparison
        // });

        // If we have cached data, it's not expired, and the version matches
        if (cachedData && cachedTimestamp && cachedVersion === cacheVersion) {
          const cacheAge = currentTime - parseInt(cachedTimestamp);
          const ttlMs = cacheTTL * 1000; // Convert seconds to milliseconds

          if (cacheAge < ttlMs) {
            // console.log('Using cached Rentman data - Cache is valid');
            setData(JSON.parse(cachedData));
            setLoading(false);
            return;
          } else {
            // console.log('Cache expired - Age:', cacheAge, 'TTL:', ttlMs);
          }
        } else {
          // console.log('Cache invalid - Fetching fresh data');
        }

        // If no cache, expired, or version mismatch, fetch fresh data
        const baseUrl = import.meta.env.DEV 
          ? 'http://localhost:5001/dagslys-dc425/europe-west1'
          : 'https://europe-west1-dagslys-dc425.cloudfunctions.net';
        
        const response = await fetch(`${baseUrl}/getRentmanData`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (!result) {
          throw new Error("No data received from the server.");
        }

        // console.log('Fresh data fetched - Updating cache with version:', cacheVersion);

        // Update cache with new data and version
        updateCache(result, cacheVersion);
        
        setData(result);
      } catch (err) {
        console.error("Failed to fetch Rentman data:", err);
        setError(err.message || "An error occurred while fetching data.");
      }
      setLoading(false);
    };

    fetchData();
  }, [cacheVersion, cacheTTL]); // Re-run when cache version or TTL changes

  // Function to force a refresh of the data
  const refreshData = async () => {
    clearCache();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.DEV 
        ? 'http://localhost:5001/dagslys-dc425/europe-west1'
        : 'https://europe-west1-dagslys-dc425.cloudfunctions.net';
      
      // First, get the current cache version from Firestore
      const cacheVersionRef = doc(db, 'system', 'cacheVersion');
      const versionDoc = await getDoc(cacheVersionRef);
      const currentVersion = versionDoc.exists && versionDoc.data() && typeof versionDoc.data().version !== 'undefined' 
        ? versionDoc.data().version 
        : 1;
      
      // console.log('Current Firestore cache version:', currentVersion);
      
      // Update local storage with current version
      localStorage.setItem(CACHE_VERSION_KEY, currentVersion.toString());
      
      // Fetch fresh data
      const response = await fetch(`${baseUrl}/getRentmanData`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result) {
        throw new Error("No data received from the server.");
      }

      // Wait a short moment to ensure version propagation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Double-check the version hasn't changed
      const updatedVersionDoc = await getDoc(cacheVersionRef);
      const updatedVersion = updatedVersionDoc.exists && updatedVersionDoc.data() && typeof updatedVersionDoc.data().version !== 'undefined'
        ? updatedVersionDoc.data().version 
        : 1;
      
      // console.log('Updated Firestore cache version:', updatedVersion);

      // Update cache with new data and version
      updateCache(result, updatedVersion);
      
      setData(result);
      setCacheVersion(updatedVersion);
    } catch (err) {
      console.error("Failed to refresh Rentman data:", err);
      setError(err.message || "An error occurred while refreshing data.");
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refreshData, clearCache };
}